import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, AlertTriangle, CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

/**
 * SubscriptionCancellationCard — Kündigung & Reaktivierung des Abos.
 *
 * Flow:
 *  - Aktiv:       Button „Abo kündigen" → Dialog mit Bestätigung & optionaler Begründung
 *  - Gekündigt:   Info-Box mit Effective-Date + „Kündigung zurücknehmen"
 *  - Gesperrt:    Button „Abo reaktivieren"
 */
export default function SubscriptionCancellationCard() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get("/billing/me/subscription");
      setSub(data);
    } catch {
      setSub(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let stopped = false;
    Promise.resolve().then(() => { if (!stopped) load(); });
    return () => { stopped = true; };
  }, [load]);

  const confirmCancel = async () => {
    setBusy(true);
    await api.post("/billing/cancel", { reason: reason.trim() || null })
      .then(() => {
        toast.success("Kündigung registriert. Dein Zugang bleibt bis zum Ende der Frist aktiv.");
        setDialogOpen(false);
        setReason("");
        load();
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Kündigung fehlgeschlagen");
      });
    setBusy(false);
  };

  const undoCancel = async () => {
    setBusy(true);
    await api.post("/billing/cancel/undo")
      .then(() => {
        toast.success("Kündigung zurückgenommen.");
        load();
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Konnte Kündigung nicht zurücknehmen");
      });
    setBusy(false);
  };

  const reactivate = async () => {
    setBusy(true);
    await api.post("/billing/reactivate")
      .then(() => {
        toast.success("Abo reaktiviert — willkommen zurück!");
        load();
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Reaktivierung fehlgeschlagen");
      });
    setBusy(false);
  };

  if (loading) {
    return (
      <Card data-testid="subscription-card">
        <CardContent className="py-10 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
        </CardContent>
      </Card>
    );
  }

  if (!sub || sub.is_admin) return null;

  const status = sub.status;
  const planLabel = sub.plan?.name || sub.plan_id || "—";
  const hasPendingCancel = sub.has_pending_cancellation;
  const isCancelled = status === "cancelled";

  return (
    <>
      <Card data-testid="subscription-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">Abonnement & Kündigung</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Aktueller Plan */}
          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-zinc-500">Plan</div>
              <div className="font-display font-semibold mt-1" data-testid="sub-plan-name">{planLabel}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-zinc-500">Status</div>
              <div className="font-display font-semibold mt-1" data-testid="sub-status">
                {status === "active" && <span className="text-emerald-700">Aktiv</span>}
                {status === "trialing" && <span className="text-cyan-700">Testphase</span>}
                {status === "cancelled" && <span className="text-zinc-500">Gekündigt</span>}
                {status === "trial_expired" && <span className="text-rose-700">Trial abgelaufen</span>}
                {!["active", "trialing", "cancelled", "trial_expired"].includes(status) && status}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-zinc-500">Min-Laufzeit endet</div>
              <div className="font-display font-semibold mt-1" data-testid="sub-min-end">{fmtDate(sub.min_end_at)}</div>
            </div>
          </div>

          {/* Hinweis-Box bei offener Kündigung */}
          {hasPendingCancel && !isCancelled && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 flex items-start gap-3" data-testid="cancel-pending-info">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-amber-900">
                  Kündigung beantragt — Zugang endet am {fmtDate(sub.cancellation_effective_at)}
                </div>
                <p className="text-sm text-amber-800 mt-1">
                  Du kannst die App bis dahin uneingeschränkt nutzen. Deine Daten bleiben für unsere
                  Buchhaltung archiviert, wie es das HGB vorschreibt.
                </p>
                {sub.cancellation_reason && (
                  <p className="text-xs text-amber-700 mt-2 italic">
                    Begründung: &bdquo;{sub.cancellation_reason}&ldquo;
                  </p>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3"
                  onClick={undoCancel}
                  disabled={busy}
                  data-testid="sub-cancel-undo-btn"
                >
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
                  Kündigung zurücknehmen
                </Button>
              </div>
            </div>
          )}

          {/* Hinweis-Box wenn bereits gesperrt */}
          {isCancelled && (
            <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-4 flex items-start gap-3" data-testid="cancel-effective-info">
              <CheckCircle2 className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-rose-900">
                  Dein Abo ist gekündigt.
                </div>
                <p className="text-sm text-rose-800 mt-1">
                  Du kannst dein Abo jederzeit reaktivieren — deine Daten sind alle noch da.
                </p>
                <Button
                  size="sm"
                  className="mt-3 brand-gradient-bg text-white"
                  onClick={reactivate}
                  disabled={busy || !sub.plan_id}
                  data-testid="sub-reactivate-btn"
                >
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Abo reaktivieren
                </Button>
              </div>
            </div>
          )}

          {/* Kündigungs-CTA — nur wenn aktiv und noch nicht gekündigt */}
          {sub.can_cancel && (
            <div className="pt-3 border-t border-zinc-100">
              <p className="text-sm text-zinc-600 mb-3">
                Du kannst dein Abo jederzeit kündigen. Die Kündigung wird nach{" "}
                <strong>3 Monaten Frist</strong> wirksam — bis dahin nutzt du die App ganz normal weiter.
              </p>
              <Button
                variant="outline"
                className="border-rose-200 text-rose-700 hover:bg-rose-50"
                onClick={() => setDialogOpen(true)}
                data-testid="sub-cancel-btn"
              >
                Abo kündigen
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bestätigungs-Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="cancel-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Abo wirklich kündigen?</DialogTitle>
            <DialogDescription>
              Die Kündigung wird in 3 Monaten wirksam. Bis dahin kannst du Easy Gutachter
              uneingeschränkt nutzen. Danach wird dein Zugang gesperrt — deine Daten bleiben
              jedoch archiviert (gesetzliche Aufbewahrungspflicht).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <label className="text-sm font-semibold text-zinc-700">
              Begründung (freiwillig)
            </label>
            <Textarea
              placeholder="Was hat dich zur Kündigung bewegt? (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              rows={3}
              data-testid="cancel-reason-textarea"
            />
            <div className="text-xs text-zinc-400 text-right">{reason.length}/500</div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={busy} data-testid="cancel-dialog-back-btn">
              Abbrechen
            </Button>
            <Button
              className="border-rose-200 text-white bg-rose-600 hover:bg-rose-700"
              onClick={confirmCancel}
              disabled={busy}
              data-testid="cancel-dialog-confirm-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Jetzt kündigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
